import React, { useState, useEffect } from 'react';
import ResetPasswordMessage from './resetPasswordMessage';
import { Modal, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useAppSelector } from '../../redux/hooks';

interface UpdatePasswordModalProps {
  show: boolean;
  onHide: () => void;
}

const UpdatePasswordModal: React.FC<UpdatePasswordModalProps> = ({ show, onHide }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [mustChange, setMustChange] = useState(false);
  const [passwordSettings, setPasswordSettings] = useState({ PasswordLength: 0, PasswordComplexity: false });
  const sessionInfo = useAppSelector((state) => state.SessionInfo);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (show) {
      // Reset all state when the modal is shown
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      checkMustChange();
      fetchPasswordSettings();
    }
  }, [show]);

  const fetchPasswordSettings = async () => {
    try {
      const response = await fetch(
        `/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${sessionInfo.firm.id}/uv/get/passwordsettings?SessionID=${sessionInfo.sid}`
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.ok) {
        setPasswordSettings({
          PasswordLength: data.result.PasswordLength || 0,
          PasswordComplexity: data.result.PasswordComplexity || false
        });
      }
    } catch (error) {
      console.error('Error fetching password settings:', error);
      // Use defaults if API call fails
      setPasswordSettings({ PasswordLength: 0, PasswordComplexity: false });
    }
  };

  const checkMustChange = async () => {
    try {
      const response = await fetch(
        `/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${sessionInfo.firm.id}/uv/get/user?SessionID=${sessionInfo.sid}&id=${sessionInfo.user.id}`
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.ok) {
        setMustChange(data.result['MustChange'] === 1 || data.result['MustChange'] === '1');
      }
    } catch (error) {
      console.error('Error checking mustChange:', error);
    }
  };

  const handleClose = () => {
    if (!mustChange) {
      onHide();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Use dynamic password length requirement
    const minLength = passwordSettings.PasswordLength > 0 ? passwordSettings.PasswordLength : 8;
    if (newPassword.length < minLength) {
      setError(`Password must be at least ${minLength} characters long`);
      return;
    }

    // Use dynamic complexity requirement
    if (passwordSettings.PasswordComplexity) {
      if (!/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
        setError('Password must contain at least one letter and one number');
        return;
      }
    }

    if (/[^a-zA-Z0-9!@#$^&*\-_]/.test(newPassword)) {
      setError('Password contains invalid special characters');
      return;
    }

    try {
      // First, verify the current password
      const verifyResponse = await fetch(
        `/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${sessionInfo.firm.id}/uv/verify/password?SessionID=${sessionInfo.sid}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            SubUserID: sessionInfo.user.id,
            SubUsername: sessionInfo.user.account,
            Password: currentPassword,
          }),
        }
      );

      if (!verifyResponse.ok) {
        throw new Error('Failed to verify password');
      }

      const verifyData = await verifyResponse.json();

      if (!verifyData.ok) {
        setError(verifyData.error || 'Current password is incorrect');
        return;
      }

      // If current password is verified, proceed with updating the password
      const updateResponse = await fetch(
        `/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${sessionInfo.firm.id}/uv/update/password?SessionID=${sessionInfo.sid}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            SubUserID: sessionInfo.user.id,
            SubUsername: sessionInfo.user.account,
            Password: newPassword,
          }),
        }
      );

      const updateData = await updateResponse.json();

      if (updateData.ok) {
        alert("Password updated successfully");
        setMustChange(false);
        onHide();
      } else {
        setError(updateData.error || 'An error occurred while updating the password');
      }
    } catch (error) {
      console.error('Password update error:', error);
    }
  };

  const togglePasswordVisibility = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setter(prev => !prev);
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={!mustChange}>
      <Modal.Header closeButton>
        <Modal.Title>Update Password</Modal.Title>
      </Modal.Header>

    <Form onSubmit={handleSubmit}>
      <Modal.Body>{mustChange && (
          <Alert variant="warning">
            Your administrator has requested that you update your password.
          </Alert>
        )}

        <ResetPasswordMessage 
          PasswordLength={passwordSettings.PasswordLength}
          PasswordComplexity={passwordSettings.PasswordComplexity}
        />
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3" controlId="currentPassword">
            <Form.Label>Current Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showCurrentPassword ? "text" : "password"}
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <InputGroup.Text 
                onClick={() => togglePasswordVisibility(setShowCurrentPassword)}
              >
                {showCurrentPassword ? <i className="fa-solid fa-eye-slash"></i> : <i className="fa-solid fa-eye"></i>}
              </InputGroup.Text >
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3" controlId="newPassword">
            <Form.Label>New Password</Form.Label>
            <InputGroup>
              <Form.Control
                type={showNewPassword ? "text" : "password"}
                placeholder="enter new password.."
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <InputGroup.Text 
                onClick={() => togglePasswordVisibility(setShowNewPassword)}
              >
                {showNewPassword ? <i className="fa-solid fa-eye-slash"></i> : <i className="fa-solid fa-eye"></i>}
              </InputGroup.Text >
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3" controlId="confirmPassword">
            <Form.Label>Verify New Password</Form.Label>
                <InputGroup>
                    <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="confirm new password.."
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                    />
                    <InputGroup.Text 
                        onClick={() => togglePasswordVisibility(setShowConfirmPassword)}
                    >
                        {showConfirmPassword ? <i className="fa-solid fa-eye-slash"></i> : <i className="fa-solid fa-eye"></i>}
                    </InputGroup.Text>
                </InputGroup>
          </Form.Group>

      </Modal.Body>

      <Modal.Footer>

          <Button variant="primary" type="submit">
            Update Password
          </Button>
          </Modal.Footer>
        </Form>
    </Modal>
  );
};

export default UpdatePasswordModal;

